import { useCallback, useEffect, useState } from 'react';
import { callScheduleQueryRpc } from '../lib/scheduleRpc';

export interface ScheduleYearDensityRow {
  day_date: string;
  job_count: number;
  booking_count: number;
  shift_count: number;
  personal_count: number;
  subscribed_count: number;
}

export interface UseScheduleYearDensityResult {
  rows: ScheduleYearDensityRow[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useScheduleYearDensity(
  businessId: string | undefined,
  year: number,
): UseScheduleYearDensityResult {
  const [rows, setRows] = useState<ScheduleYearDensityRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!businessId) {
      setRows([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    const result = await callScheduleQueryRpc<ScheduleYearDensityRow[]>(
      'get_schedule_year_density',
      {
        p_business_id: businessId,
        p_year: year,
      },
    );

    if (!result.success) {
      setRows([]);
      setError(result.error.message ?? 'Failed to load year density');
      setLoading(false);
      return;
    }

    setRows(Array.isArray(result.data) ? result.data : []);
    setLoading(false);
  }, [businessId, year]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch triggered on dependency change; state updates happen after RPC resolves
    void refresh();
  }, [refresh]);

  return { rows, loading, error, refresh };
}
